import React, { Dispatch, SetStateAction, useContext, useEffect, useState } from "react";
import { Form, Spinner } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { checkExistCustomerEnquiry, createCustomer, updateCustomer } from "../../../../api/customer.api";
import SnackbarContext from "../../../../context/snackbar.context";
import { SelectedUserContext } from "../../../../context/selecteduser.context";
import { useNavigate } from "react-router-dom";
import CustomSelect from "../../../../components/CustomSelect/CustomSelect";
import SearchableCountryCodeDropdown from "../../../../components/SearchableCountryCodeDropdown/SearchableCountryCodeDropdown";
import PhoneInput from "../../../../components/PhoneInput/PhoneInput";
import countryCodeData from "../../../../assets/countryCodes.json"

interface CustomerFormProp {
  setStepNumber: Dispatch<SetStateAction<number>>;
  setCustomerEnquiryData: any;
}
const initialValues = {
  email: "",
  firstName: "",
  lastName: "",
  countryCode: "+91",
  phoneNumber: "",
  whatsappCountryCode: "+91",
  whatsappNumber: "",
  accountSource: "",
};

const validationSchema = Yup.object().shape({
  firstName: Yup.string()
    .required("First name is required")
    .matches(/^[a-zA-Z\s]{1,25}$/, "1-25 alphabets only"),
  lastName: Yup.string()
    .required("Last name is required")
    .matches(/^[a-zA-Z\s]{1,25}$/, "1-25 alphabets only"),
  email: Yup.string()
    .required("Email is required")
    .matches(/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9]{2,})+$/, "Invalid email format"),
  phoneNumber: Yup.string()
    .required("Contact number is required"),
});

const accountSourceOptions = [
  { value: "website", label: "Website" },
  { value: "campaign", label: "Campaign" },
  { value: "call_request", label: "Call Request" },
  { value: "social_media", label: "Social Media" },
];

const CustomerForm: React.FC<CustomerFormProp> = (props) => {
  const navigate = useNavigate();
  const { setStepNumber, setCustomerEnquiryData } = props;

  const snackbarCtx = useContext(SnackbarContext);
  const { selectedUser, handleSelectedUser, handleExistingEnquiry } = useContext(SelectedUserContext);
  
  const [sameNumber, setSameNumber] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedCountryCode, setSelectedCountryCode] = useState({
    contactCountryCode: countryCodeData.find((o) => o.dial_code === selectedUser?.countryCode)?.code || "IN",
    whatsappCountryCode: countryCodeData.find((o) => o.dial_code === selectedUser?.whatsappCountryCode)?.code || "IN",
  });
  const [phoneNumberError, setPhoneNumberError] = useState({ contactNumber: "", whatsappNumber: "" });
  
  const { values, errors, touched, handleChange, handleSubmit, handleBlur, setValues, setFieldValue, setErrors, setTouched } =
  useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      const filteredValues = Object.fromEntries(Object.entries(values).filter(([_, value]) => value !== ""));
      if (!filteredValues?.whatsappNumber) delete filteredValues?.whatsappCountryCode;
      if (!!phoneNumberError.contactNumber || !!phoneNumberError.whatsappNumber) return;
      submitCustomerDetails(filteredValues);
    },
  });

  useEffect(() => {
    if (!!selectedUser?.phoneNumber && selectedUser?.phoneNumber === selectedUser?.whatsappNumber) setSameNumber(true);
    setValues({
      email: selectedUser?.email || "",
      firstName: selectedUser?.firstName || "",
      lastName: selectedUser?.lastName || "",
      countryCode: selectedUser?.countryCode || "+91",
      phoneNumber: selectedUser?.phoneNumber || "",
      whatsappCountryCode: selectedUser?.whatsappCountryCode || "+91",
      whatsappNumber: selectedUser?.whatsappNumber || "",
      accountSource: selectedUser?.accountSource || "",
    });
    setSelectedCountryCode({
      contactCountryCode: countryCodeData.find((o) => o.dial_code === selectedUser?.countryCode)?.code || "IN",
      whatsappCountryCode: countryCodeData.find((o) => o.dial_code === selectedUser?.whatsappCountryCode)?.code || "IN",
    });
    setTouched({ email: false, firstName: false, lastName: false, phoneNumber: false });
    setErrors({ email: "", firstName: "", lastName: "", phoneNumber: "" });
  }, [selectedUser]);

  const submitCustomerDetails = async (payload: any) => {
    if (!selectedUser?.id) {
      await handleCreate(payload);
    } else {
      await handleUpdate(payload);
    }
  };

  const handleCreate = async (payload: any) => {
    setLoading(true);
    const data = await createCustomer(payload);
    if (data.status === 201 || data.status === 200) {
      handleSelectedUser(data.result);
      if (data?.message?.includes("Updated")) {
        snackbarCtx.toggleSnackbar({
          message: "Customer updated Successfully",
          type: "SUCCESS",
          visible: true,
        });
        const existEnquiry = await checkExistCustomerEnquiry(data.result?.id);
        if (existEnquiry.status === 200 && existEnquiry?.result?.id) {
          handleExistingEnquiry(existEnquiry?.result);
          setCustomerEnquiryData(existEnquiry?.result);
        }
      } else {
        snackbarCtx.toggleSnackbar({
          message: "Customer added successfully",
          type: "SUCCESS",
          visible: true,
        });
      }
      navigate(`/add-leads/2/${data?.result?.id}`);
      setStepNumber(2);
    } else {
      snackbarCtx.toggleSnackbar({
        message: "Something went wrong",
        type: "FAILED",
        visible: true,
      });
    }
    setLoading(false);
  };

  const handleUpdate = async (payload: any) => {
    setLoading(true);
    const updatedData = {
      id: selectedUser?.id,
      ...payload,
    };
    const data = await updateCustomer(updatedData, selectedUser?.id);
    if (data.status === 201 || data.status === 200) {
      handleSelectedUser({ ...selectedUser, ...updatedData });
      snackbarCtx.toggleSnackbar({
        message: "Customer updated Successfully",
        type: "SUCCESS",
        visible: true,
      });
      const existEnquiry = await checkExistCustomerEnquiry(selectedUser?.id);
      if (existEnquiry.status === 200 && existEnquiry?.result?.id) {
        handleExistingEnquiry(existEnquiry?.result);
        setCustomerEnquiryData(existEnquiry?.result);
      } else {
        navigate(`/add-leads/2/${selectedUser?.id}`);
        setStepNumber(2);
      }
    } else {
      snackbarCtx.toggleSnackbar({
        message: "Something went wrong",
        type: "FAILURE",
        visible: true,
      });
    }
    setLoading(false);
  };

  useEffect(() => {
    if (sameNumber) {
      setFieldValue("whatsappCountryCode", values.countryCode);
      setFieldValue("whatsappNumber", values.phoneNumber);
      setSelectedCountryCode(prevData => ({ ...prevData, whatsappCountryCode: prevData.contactCountryCode }));
    }
  }, [values.countryCode, values.phoneNumber]);

  return (
    <form onSubmit={handleSubmit}>
      <div
        className="text-[#3A3A3A] mx-0 mt-3 flex gap-10"
        style={{ fontSize: "16px", lineHeight: "20.83px", fontWeight: "400" }}
      >
        <div className="py-2 px-0 w-100">
          <p className="m-0 mb-2">First Name</p>
          <input
            type="text"
            name="firstName"
            id="firstName"
            className="login-input w-100"
            placeholder="Enter First Name"
            value={values.firstName}
            onChange={handleChange}
            onBlur={handleBlur}
          />
          {errors.firstName && touched.firstName && (
            <div className="text-red-500 mt-1 text-[14px]">{errors.firstName}</div>
          )}
        </div>
        <div className="py-2 px-0 w-100">
          <p className="m-0 mb-2">Last Name</p>
          <input
            type="text"
            name="lastName"
            id="lastName"
            className="login-input w-100"
            placeholder="Enter Last Name"
            value={values.lastName}
            onChange={handleChange}
            onBlur={handleBlur}
          />
          {errors.lastName && touched.lastName && (
            <div className="text-red-500 mt-1 text-[14px]">{errors.lastName}</div>
          )}
        </div>
        <div className="py-2 px-0 w-100">
          <p className="m-0 mb-2">Email ID</p>
          <input
            type="text"
            name="email"
            id="email"
            className={`login-input w-100 ${!!selectedUser?.email ? "cursor-not-allowed" : ""}`}
            placeholder="Enter Mail ID"
            value={values.email}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={!!selectedUser?.email}
          />
          {errors.email && touched.email && <div className="text-red-500 mt-1 text-[14px]">{errors.email}</div>}
        </div>
      </div>
      <div
        className="text-[#3A3A3A] mx-0 mt-3 flex gap-10"
        style={{ fontSize: "16px", lineHeight: "20.83px", fontWeight: "400" }}
      >
        <div className="py-2 px-0 w-100">
          <p className="m-0 mb-2">Contact Number</p>
          <div className="flex items-center gap-2">   
            <SearchableCountryCodeDropdown defaultVal={values.countryCode} onChange={(value, selectedCountryCode) => { setFieldValue("countryCode", value); setFieldValue("phoneNumber", ""); setSelectedCountryCode(prevData => ({...prevData, contactCountryCode: selectedCountryCode})); }} disabled={!!selectedUser?.phoneNumber} /> 
            <PhoneInput
              defaultCountryCode={selectedCountryCode.contactCountryCode}
              type="number"
              name="phoneNumber"
              id="phoneNumber"
              className={`login-input w-100 ${!!selectedUser?.phoneNumber ? "cursor-not-allowed" : ""}`}
              placeholder="Enter Contact Number"
              value={values.phoneNumber}
              onPhoneNumberChange={(formattedNumber, _, errorMessage) => { setFieldValue("phoneNumber", formattedNumber); setPhoneNumberError(prevData => ({...prevData, contactNumber: errorMessage})) }}
              onBlur={handleBlur}
              disabled={!!selectedUser?.phoneNumber}
            />
          </div>
          {(errors.phoneNumber && touched.phoneNumber || !!phoneNumberError.contactNumber) && (
            <div className="text-red-500 mt-1 text-[14px]">{errors.phoneNumber || phoneNumberError.contactNumber}</div>
          )}
        </div>
        <div className="py-2 px-0 w-100">
          <p className="m-0 mb-2">Whatsapp Number</p>
          <div className="flex items-center gap-2">
            <SearchableCountryCodeDropdown hardCodedValue={sameNumber ? values.countryCode : ""} defaultVal={values.whatsappCountryCode} onChange={(value, selectedCountryCode) => { setFieldValue("whatsappCountryCode", value); setSelectedCountryCode(prevData => ({...prevData, whatsappCountryCode: selectedCountryCode}));}} disabled={sameNumber} />
            <PhoneInput
              defaultCountryCode={selectedCountryCode.whatsappCountryCode}
              type="number"
              name="whatsappNumber"
              id="whatsappNumber"
              className={`login-input w-100 ${!!selectedUser?.phoneNumber ? "cursor-not-allowed" : ""}`}
              placeholder="Enter Whatsapp Number"
              value={values.whatsappNumber}
              onPhoneNumberChange={(formattedNumber, _, errorMessage) => { setFieldValue("whatsappNumber", formattedNumber); setPhoneNumberError(prevData => ({...prevData, whatsappNumber: errorMessage})) }}
              onBlur={handleBlur}
              disabled={sameNumber}
            />
          </div>  
          <p className="m-0 mb-2 mt-2 text-secondary d-flex align-items-center ">
            <Form.Check
              type="checkbox"
              name="sameNumber"
              id="sameNumber"
              className="my-auto quotation-checkbox me-2"
              checked={sameNumber}
              onChange={(e) => {
                if (e.target.checked && !!values.phoneNumber) {
                  setSameNumber(true);
                  setValues({ ...values, whatsappNumber: values.phoneNumber, whatsappCountryCode: values.countryCode });
                } else {
                  setSameNumber(false);
                  setFieldValue("whatsappCountryCode", "+91");
                  setFieldValue("whatsappNumber", "");     
                  setPhoneNumberError(prevData => ({ ...prevData, whatsappNumber: "" }));
                  setSelectedCountryCode(prevData => ({ ...prevData, whatsappCountryCode: "IN" }));
                }
              }}
            />
            <label htmlFor="sameNumber" className="pt-0.5" style={{ fontSize: "14px", fontWeight: "400" }}>
              Same as Contact Number
            </label>
          </p>
          {(errors.whatsappNumber && touched.whatsappNumber || !!phoneNumberError.whatsappNumber) && (
            <div className="text-red-500 mt-1 text-[14px]">{errors.whatsappNumber || phoneNumberError.whatsappNumber}</div>
          )}
        </div>
        <div className="py-2 px-0 w-100">
          <CustomSelect
            name="accountSource"
            onChange={(option) => setFieldValue("accountSource", option.value)}
            label={"Account Source"}
            options={accountSourceOptions}
            value={values?.accountSource}
            clx="customer-account-source h-[18.5px] pb-1 mt-1"
            disabled={selectedUser?.accountSource ? true : false}
          />
        </div>
      </div>
      <div className="flex justify-content-end">
        <button
          type="submit"
          className="btn text-light px-3 min-w-[160px]"
          style={{ background: "linear-gradient(305.75deg, #92278F -144.06%, #EC7765 147.18%)" }}
        >
          {loading && <Spinner animation="border" size="sm" className="mr-3" />}
          Save
        </button>
      </div>
    </form>
  );
};
export default CustomerForm;
